// =============================================================================
// GlassPill — THE single source of truth for segmented filters & chips.
// Replaces the ad-hoc pill styles scattered across screens (the cause of the
// stretched / duplicate pill bugs). Active = primary fill; inactive = glass.
// Use GlassPillGroup to lay out a horizontal, content-sized (not stretched) row.
//
// BARE-RN: @expo/vector-icons Ionicons → local Icon shim.
// =============================================================================
import React from 'react';
import { View, Text, ScrollView, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { Ionicons, IoniconName } from '../ui/Icon';
import GlassSurface from './GlassSurface';
import { glassTokens } from './tokens';
import { colors, fonts, spacing } from '../../constants/theme';

export interface GlassPillProps {
  label: string;
  active?: boolean;
  onPress?: () => void;
  icon?: IoniconName;
  count?: number;            // optional badge (e.g. unread)
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
}

export function GlassPill({ label, active = false, onPress, icon, count, style, accessibilityLabel }: GlassPillProps) {
  return (
    <GlassSurface
      variant="pill"
      radius={glassTokens.radius.pill}
      surfaceColor={active ? glassTokens.surface.pillActive : undefined}
      borderColor={active ? glassTokens.surface.pillActive : glassTokens.border}
      sheen={!active}
      shadow={active}
      onPress={onPress}
      accessibilityLabel={accessibilityLabel ?? label}
      hitSlop={6}
      style={style}
      contentStyle={styles.content}
    >
      {icon ? <Ionicons name={icon} size={14} color={active ? '#FFFFFF' : colors.ink2} /> : null}
      <Text style={[styles.label, active && styles.labelActive]} numberOfLines={1}>{label}</Text>
      {typeof count === 'number' && count > 0 ? (
        <View style={[styles.badge, active && styles.badgeActive]}>
          <Text style={[styles.badgeText, active && styles.badgeTextActive]}>{count > 99 ? '99+' : count}</Text>
        </View>
      ) : null}
    </GlassSurface>
  );
}

// Content-sized horizontal row (scrolls when it overflows). Never stretches pills
// to full width — that was the bug.
export function GlassPillGroup({ children, style }: { children: React.ReactNode; style?: StyleProp<ViewStyle> }) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={[styles.group, style]}
    >
      {children}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs + 3,
    alignSelf: 'flex-start',
  },
  group: { flexDirection: 'row', gap: spacing.sm, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
  label: { ...fonts.semibold, fontSize: 13, color: colors.ink2 },
  labelActive: { color: '#FFFFFF' },
  badge: { minWidth: 18, height: 18, borderRadius: 9, paddingHorizontal: 5, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primary },
  badgeActive: { backgroundColor: 'rgba(255,255,255,0.28)' },
  badgeText: { ...fonts.bold, fontSize: 10, color: '#FFFFFF' },
  badgeTextActive: { color: '#FFFFFF' },
});

export default GlassPill;
